import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import {
  BASIC_BLACK,
  BASIC_DARK_GRAY,
  BASIC_GRAY,
  BASIC_WHITE,
  BASIC_YELLOW,
} from "../../constants/colors";
import IconSelectDropDown from "../../icons/IconSelectDropDown";

type SelectOptionType = {
  id: number;
  name: string;
  value: any;
};

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
  options: SelectOptionType[];
  disabled?: boolean;
  onChange: (name: string, value: any) => void;
  size?: "sm" | "md";
  clearable?: boolean;
  padding?: string;
  top?: string;
  width?: string;
};

export const Select: React.FC<Props> = ({
  name,
  label,
  placeholder = "--",
  required,
  defaultValue,
  options,
  disabled,
  onChange,
  size = "md",
  clearable = true,
  padding,
  top,
  width,
}) => {
  const uuid = useMemo(() => {
    return uuidv4();
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [currentName, setCurrentName] = useState(defaultValue ?? "");

  const formContext = useFormContext();

  const handleOpenSelection = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleChange = (optionName: string, value: string, event?: any) => {
    setIsOpen(false);
    if (clearable && currentName === optionName) {
      setCurrentName(placeholder ?? "");
      onChange("", "");
      try {
        if (name) formContext.setValue(name, "", { shouldValidate: true });
      } catch (err) {}
      return;
    }

    setCurrentName(optionName);
    onChange(optionName, value);
    try {
      if (name) formContext.setValue(name, value, { shouldValidate: true });
    } catch (err) {}
  };

  useEffect(() => {
    try {
      if (!formContext.getValues(name)) setCurrentName(placeholder ?? "");
    } catch (err) {}
  }, [options]);

  const closeDropdown = useCallback((e: MouseEvent) => {
    if (
      e.target instanceof HTMLElement &&
      (e.target.id === `container-${uuid}` ||
        e.target.id === `select-container-${uuid}` ||
        e.target.id === `select-placeholder-${uuid}` ||
        e.target.id === `select-dropdown-${uuid}`)
    )
      return;

    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (defaultValue) {
      try {
        formContext.setValue(name, defaultValue, { shouldValidate: true });
      } catch (err) {}
    }
  }, []);

  useEffect(() => {
    document.body.addEventListener("click", closeDropdown);

    return () => {
      document.body.removeEventListener("click", closeDropdown);
    };
  }, []);

  return (
    <Container id={`container-${uuid}`}>
      {label && <label>{label}</label>}

      <SelectContainer
        id={`select-container-${uuid}`}
        data-testid="select-container"
        selected={Boolean(currentName)}
        onClick={(e) => {
          e.stopPropagation();
          handleOpenSelection();
        }}
        disabled={disabled ?? false}
        padding={padding}
        width={width}
      >
        <Placeholder
          id={`select-placeholder-${uuid}`}
          size={size}
          $isPlaceholder={currentName === "" || currentName === placeholder}
          padding={padding}
        >
          {currentName !== "" ? currentName : placeholder}
        </Placeholder>
        <DropdownIconContainer
          id={`select-dropdown-${uuid}`}
          disabled={disabled ?? false}
        >
          <IconSelectDropDown />
        </DropdownIconContainer>
      </SelectContainer>

      {isOpen && !disabled && (
        <DropdownContainer
          data-testid="dropdown-container"
          size={size}
          top={top}
        >
          {options?.map((option) => {
            return (
              <DropdownOption
                data-testid="option"
                selected={currentName === option.name}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChange(option.name, option.value);
                }}
                key={option.id}
              >
                {option.name}
              </DropdownOption>
            );
          })}
        </DropdownContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 10px 0 0;
  label {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 6px;
  }
`;

const SelectContainer = styled.div<{
  selected: boolean;
  disabled: boolean;
  padding?: string;
  width?: string;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: ${({ width }) => width || "100%"};
  padding: ${({ padding }) => padding || "0"};
  border: solid 1px ${BASIC_DARK_GRAY};
  cursor: pointer;
  background-color: ${BASIC_WHITE};
  color: ${({ selected }) => (selected ? BASIC_BLACK : BASIC_GRAY)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const Placeholder = styled.div<{
  size: "sm" | "md";
  $isPlaceholder: boolean;
  padding?: string;
}>`
  font-size: 16px;
  font-weight: ${({ $isPlaceholder }) => ($isPlaceholder ? "normal" : "600")};
  padding: ${({ padding, size }) =>
    padding || (size === "sm" ? "8px 20px" : "14px 20px")};
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? BASIC_GRAY : BASIC_BLACK)};
`;

const DropdownIconContainer = styled.div<{ disabled: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 10px;
`;

const DropdownContainer = styled.div<{ size: "sm" | "md"; top?: string }>`
  position: absolute;
  top: ${({ top, size }) => top || (size === "sm" ? "40px" : "55px")};
  width: 100%;
  z-index: 100;
  max-height: 200px;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: ${BASIC_WHITE};
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
`;

const DropdownOption = styled.div<{ selected: boolean }>`
  cursor: pointer;
  padding: 8px 20px;
  display: flex;
  align-items: center;
  transition: background-color 0.3s ease;
  background-color: ${({ selected }) =>
    selected ? BASIC_YELLOW : BASIC_WHITE};
  &:hover {
    background-color: ${({ selected }) =>
      selected ? BASIC_YELLOW : BASIC_BLACK};
    color: ${({ selected }) => (selected ? BASIC_BLACK : BASIC_WHITE)};
  }
`;
